import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Modal } from '../../components/Modal';
import { listBranches } from '../../data/fetchers';
import { upsertUser } from '../../data/mutators';
import type { User } from '../../types/domain';

const blank = (): User => ({
  id: '',
  name: '',
  phone: '',
  email: '',
  role: 'cashier',
  branchIds: [],
  active: true,
  pin: '',
});

const randomPin = () => String(Math.floor(1000 + Math.random() * 9000));

export function UserFormModal({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: User | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [form, setForm] = useState<User>(blank());
  const [dashboardPassword, setDashboardPassword] = useState('');
  const [showDashboardPassword, setShowDashboardPassword] = useState(false);
  const [branchError, setBranchError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(user ? { ...user } : blank());
      setDashboardPassword('');
      setShowDashboardPassword(false);
      setBranchError('');
    }
  }, [open, user]);

  const mutation = useMutation({
    mutationFn: upsertUser,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const set = <K extends keyof User>(k: K, v: User[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id: string) =>
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(id)
        ? f.branchIds.filter((x) => x !== id)
        : [...f.branchIds, id],
    }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !(form.phone ?? '').trim()) return;
    if (!user && dashboardPassword.trim().length < 8) return;
    if (form.branchIds.length === 0) {
      setBranchError('กรุณาเลือกอย่างน้อย 1 สาขา');
      return;
    }
    mutation.mutate({
      ...form,
      dashboardPassword: dashboardPassword.trim() || undefined,
    }); // id '' for new → API mutator assigns a stable UUID-backed id
  };

  return (
    <Modal
      open={open}
      title={user ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้'}
      onClose={onClose}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm-label font-semibold text-ink-2">ชื่อ</span>
          <input
            aria-label="ชื่อ"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm-label font-semibold text-ink-2">
            เบอร์โทร
          </span>
          <input
            aria-label="เบอร์โทร"
            type="tel"
            inputMode="tel"
            value={form.phone ?? ''}
            onChange={(e) => set('phone', e.target.value)}
            className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            placeholder="0812345678"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm-label font-semibold text-ink-2">
            รหัสผ่านเข้า Dashboard
          </span>
          <input
            aria-label="รหัสผ่านเข้า Dashboard"
            type={showDashboardPassword ? 'text' : 'password'}
            value={dashboardPassword}
            onChange={(e) => setDashboardPassword(e.target.value)}
            className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
            placeholder={
              user ? 'กรอกเมื่อต้องการตั้งใหม่' : 'อย่างน้อย 8 ตัวอักษร'
            }
          />
          <button
            type="button"
            onClick={() => setShowDashboardPassword((value) => !value)}
            className="self-end -mt-9 mr-2 mb-2 px-2 py-1 text-xs-label text-ink-3 hover:text-primary"
            aria-label={showDashboardPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
          >
            {showDashboardPassword ? 'ซ่อน' : 'แสดง'}
          </button>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm-label font-semibold text-ink-2">บทบาท</span>
          <select
            aria-label="บทบาท"
            value={form.role}
            onChange={(e) => set('role', e.target.value as User['role'])}
            className="h-10 px-3 rounded-btn border border-line bg-surface text-body"
          >
            <option value="owner">เจ้าของ</option>
            <option value="manager">ผู้จัดการ</option>
            <option value="cashier">แคชเชียร์</option>
          </select>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm-label font-semibold text-ink-2">สาขา</span>
          <div className="flex flex-wrap gap-3">
            {branches.map((b) => (
              <label key={b.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.branchIds.includes(b.id)}
                  onChange={() => {
                    toggleBranch(b.id);
                    setBranchError('');
                  }}
                />
                <span className="text-body">{b.short}</span>
              </label>
            ))}
          </div>
          {branchError && (
            <p role="alert" className="text-sm-label text-status-error">
              {branchError}
            </p>
          )}
        </div>
        {form.role !== 'owner' && (
          <label className="flex flex-col gap-1.5">
            <span className="text-sm-label font-semibold text-ink-2">
              PIN เข้า POS
            </span>
            <div className="flex gap-2 items-center">
              <input
                aria-label="PIN เข้า POS"
                inputMode="numeric"
                maxLength={4}
                value={form.pin ?? ''}
                onChange={(e) =>
                  set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                className="h-10 px-3 rounded-btn border border-line bg-surface text-body num w-28"
              />
              <Btn
                kind="default"
                size="sm"
                type="button"
                onClick={() => set('pin', randomPin())}
              >
                สุ่ม
              </Btn>
            </div>
          </label>
        )}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => set('active', e.target.checked)}
          />
          <span className="text-body">เปิดใช้งาน</span>
        </label>
        <div className="flex gap-2 justify-end pt-2">
          <Btn kind="default" type="button" onClick={onClose}>
            ยกเลิก
          </Btn>
          <Btn kind="primary" type="submit">
            บันทึก
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
